import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DOM_EVENT } from '@/shared/constants/keyboard'
import {
  isBeforeDay,
  isSameDay,
  useCalendar,
} from '../calendar-panel/use-calendar'
import type { DateRange } from './index'

interface UseDateRangePickerFieldProps {
  value: DateRange
  disabled: boolean
  withActions: boolean
  error?: boolean | string
  helpText?: string
  fromLabel?: string
  toLabel?: string
  onChange: (range: DateRange) => void
  onBlur?: () => void
}

export function useDateRangePickerField({
  value,
  disabled,
  withActions,
  error,
  helpText,
  fromLabel,
  toLabel,
  onChange,
  onBlur,
}: UseDateRangePickerFieldProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [hoverDate, setHoverDate] = useState<Date | undefined>(undefined)
  const [stagedRange, setStagedRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  })
  const ref = useRef<HTMLDivElement>(null)
  const { viewDate, prevMonth, nextMonth, goToDate, days } = useCalendar(
    value.start,
  )

  const activeRange = withActions ? stagedRange : value
  const hasError = !!error
  const errorMessage = typeof error === 'string' ? error : undefined
  const displayedHelp = errorMessage ?? helpText
  const fromPlaceholder = fromLabel ?? t('calendar.fromLabel')
  const toPlaceholder = toLabel ?? t('calendar.toLabel')
  const iconClassname = `pointer-events-none absolute top-1/2 right-3 h-6 w-6 -translate-y-1/2 transition text-error-50 fill-error-base stroke-2`

  function openFrom() {
    if (disabled) return
    if (withActions) {
      setStagedRange({ start: undefined, end: undefined })
    } else {
      onChange({ start: undefined, end: undefined })
    }
    setIsOpen(true)
  }

  function openTo() {
    if (disabled) return
    if (withActions) {
      const start = stagedRange.start ?? value.start
      if (start) goToDate(start)
      setStagedRange({ start, end: undefined })
    } else {
      if (!value.start) {
        onChange({ start: undefined, end: undefined })
      } else {
        goToDate(value.start)
        onChange({ start: value.start, end: undefined })
      }
    }
    setIsOpen(true)
  }

  function close() {
    setIsOpen(false)
    setHoverDate(undefined)
    onBlur?.()
  }

  function handleConfirm() {
    onChange(stagedRange)
    close()
  }

  function handleDayClick(date: Date) {
    if (withActions) {
      const { start, end } = stagedRange
      if (!start || end) {
        setStagedRange({ start: date, end: undefined })
      } else if (isBeforeDay(date, start) || isSameDay(date, start)) {
        setStagedRange({ start: date, end: undefined })
      } else {
        setStagedRange({ start, end: date })
      }
    } else {
      const { start, end } = value
      if (!start || end) {
        onChange({ start: date, end: undefined })
      } else if (isBeforeDay(date, start) || isSameDay(date, start)) {
        onChange({ start: date, end: undefined })
      } else {
        onChange({ start, end: date })
        close()
      }
    }
  }

  function triggerClasses(active: boolean, hasValue: boolean): string {
    return [
      'flex h-12 w-full items-center rounded-lg border px-4 pr-10 font-sans text-base tracking-base transition outline-none text-left',
      disabled
        ? 'cursor-not-allowed border-neutral-300 bg-neutral-50 text-neutral-300'
        : hasError
          ? 'border-error-base bg-error-50 text-neutral-300'
          : active
            ? 'border-indigo-300 bg-indigo-50 text-indigo-300'
            : hasValue
              ? 'border-neutral-300 bg-white text-neutral-900 hover:border-indigo-600'
              : 'border-neutral-300 bg-white text-neutral-300 hover:border-indigo-600',
    ]
      .filter(Boolean)
      .join(' ')
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
        setHoverDate(undefined)
      }
    }
    document.addEventListener(DOM_EVENT.MOUSEDOWN, handleClickOutside)
    return () =>
      document.removeEventListener(DOM_EVENT.MOUSEDOWN, handleClickOutside)
  }, [])

  return {
    ref,
    isOpen,
    hoverDate,
    setHoverDate,
    activeRange,
    hasError,
    displayedHelp,
    fromPlaceholder,
    toPlaceholder,
    iconClassname,
    viewDate,
    prevMonth,
    nextMonth,
    days,
    openFrom,
    openTo,
    close,
    handleConfirm,
    handleDayClick,
    triggerClasses,
  }
}
